'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { aiSearchKey } from '@/lib/ai-search';
import { getAiSearchProgress, resetAiSearchProgress } from '@/lib/ai-search-progress';
import type { FilterParams } from '@/lib/search-filter-params';
import { canUseFeature } from '@/lib/feature-access';
import { type FilterTier, tierForSession } from '@/lib/search-gating';
import { activeFilterCount } from '@/new_components/searchresults/activeFilters';
import { useLocale } from '@/hooks/use-locale';
import { useRecordRecentSearch } from '@/hooks/use-recent-activity';
import { useSession } from '@/hooks/use-session';
import type { NaceCode } from './searchPageTypes';

// The search page's mount-time and window-level effects, one hook per concern.
// SearchPageClient calls them in the order the effects ran when they were
// inline; keep that order when adding to it.

/**
 * Which rendered search an AI search produced, so its search_query log row
 * can say so (the progress itself is reset as soon as the results render).
 */
export function useAiSearchOrigin(renderedSearchParams: string) {
  const aiOriginRef = useRef<{ key: string; query: string } | null>(null);
  useEffect(() => {
    const progress = getAiSearchProgress();
    const key = aiSearchKey(new URLSearchParams(renderedSearchParams));
    if (progress.phase === 'loading' && progress.targetKey === key) {
      aiOriginRef.current = { key, query: progress.query };
      resetAiSearchProgress();
    }
  }, [renderedSearchParams]);
  return aiOriginRef;
}

/** Locale, session and the entitlements the page derives from them. */
export function useSearchViewer(initialTier: FilterTier | undefined) {
  const { locale } = useLocale();
  const { data: session, status: sessionStatus } = useSession();
  const recordRecentSearch = useRecordRecentSearch();
  // Export is shown to everyone; guests get the signup prompt instead.
  const canExport = canUseFeature(session, 'excelExport');
  // Filter gating runs off the server-known tier until the client session
  // lands, so the locks never contradict what the server will actually
  // enforce on the next request.
  const tier: FilterTier =
    sessionStatus === 'loading' && initialTier
      ? initialTier
      : tierForSession(session);
  return { locale, session, sessionStatus, recordRecentSearch, canExport, tier };
}

/**
 * Lazy-load filter data if not provided by SSR. Fire independent fetches so
 * the small NACE list lands in the sidebar immediately (~50ms) instead of
 * being held up behind the keyword scan over yearly_metadata (~3-5s). The
 * city list has its own per-country fetch (useCityList).
 */
export function useFilterLists(
  initialNaceCodes: NaceCode[],
  initialCityList: string[],
  initialTagList: string[],
) {
  const [naceCodes, setNaceCodes] = useState(initialNaceCodes);
  const [tagList, setTagList] = useState(initialTagList);
  const filtersFetchedRef = useRef(false);
  useEffect(() => {
    if (filtersFetchedRef.current) return;
    if (
      initialNaceCodes.length > 0 &&
      initialCityList.length > 0 &&
      initialTagList.length > 0
    )
      return;
    filtersFetchedRef.current = true;
    if (initialNaceCodes.length === 0) {
      fetch('/api/search/filters/nace')
        .then((res) => res.json())
        .then((data) => {
          if (data.naceCodes) setNaceCodes(data.naceCodes);
        })
        .catch(() => {});
    }
    if (initialTagList.length === 0) {
      fetch('/api/search/filters/keywords')
        .then((res) => res.json())
        .then((data) => {
          if (data.tags) setTagList(data.tags);
        })
        .catch(() => {});
    }
  }, [initialNaceCodes, initialCityList, initialTagList]);
  return { naceCodes, tagList };
}

/** Filter-panel open state and the mobile fullscreen overlay's effects. */
export function useFiltersPanel() {
  // Default closed so the mobile fullscreen overlay doesn't flash on SSR
  // hydration. Desktop opens it on mount via the effect below.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersInitRef = useRef(false);
  useEffect(() => {
    if (filtersInitRef.current) return;
    filtersInitRef.current = true;
    if (window.innerWidth >= 1024) setFiltersOpen(true); // eslint-disable-line react-hooks/set-state-in-effect -- open the panel on desktop after hydration, never during SSR (pre-existing; the eslint-disable in the old component made the compiler skip it)
  }, []);

  // Mobile flow: the header's filter button dispatches
  // `firmendata:open-filters` (see HeaderLogo), which opens the filter panel
  // fullscreen on mobile. The sidebar's own submit handler closes it again.
  useEffect(() => {
    const onOpen = () => setFiltersOpen(true);
    window.addEventListener('firmendata:open-filters', onOpen);
    return () => window.removeEventListener('firmendata:open-filters', onOpen);
  }, []);

  // Lock body scroll while the mobile fullscreen overlay is open so iOS Safari
  // doesn't leak touch scrolling to the page underneath. Desktop is unaffected
  // because the sidebar is in normal flow there.
  useEffect(() => {
    if (!filtersOpen) return;
    if (window.innerWidth >= 1024) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filtersOpen]);
  return { filtersOpen, setFiltersOpen };
}

/**
 * Desktop uses a fixed full-viewport split layout, so we lock body scroll
 * to keep the page from double-scrolling. Mobile uses the natural page flow
 * (the results list scrolls with the page), so we must NOT lock body there
 * or the page becomes unscrollable.
 */
export function useDesktopBodyLock() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < 1024) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
}

/**
 * Active filter count for the header badge, derived from the filter object
 * rather than a hand-kept list — that list had already drifted from
 * FilterParams. `q` is excluded: it is the search term, shown in the header
 * input, not a badge. Broadcast as a CustomEvent so the global HeaderLogo
 * can listen without importing search-page state.
 */
export function useFilterCountBroadcast(initialFilters: FilterParams, tier: FilterTier) {
  const filterCount = useMemo(
    () => activeFilterCount(initialFilters, tier),
    [initialFilters, tier],
  );

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('firmendata:filter-count', { detail: filterCount }),
    );
  }, [filterCount]);
}

/**
 * Cities are fetched per country and re-fetched when the scope changes, so
 * the dropdown never offers a city the current country cannot return.
 * Unlike the state list (a 16-entry constant the client can filter), there
 * are 13.6K cities and which country each belongs to is only knowable in
 * SQL. Kept out of the one-shot filter block (useFilterLists), which is
 * fire-once by design; called after it because it reads the parsed URL
 * filters.
 */
export function useCityList(cityCountry: string, initialCityList: string[]) {
  const [cityList, setCityList] = useState(initialCityList);
  useEffect(() => {
    if (initialCityList.length > 0) return;
    let cancelled = false;
    const qs = cityCountry ? `?country=${cityCountry}` : '';
    fetch(`/api/search/filters/cities${qs}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.cities) setCityList(data.cities);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cityCountry, initialCityList]);
  return cityList;
}
